import './root.css';
import './tailwind.css';

import { Links, Meta, Outlet, Scripts, ScrollRestoration } from '@remix-run/react';
import { useAuth } from '@workos-inc/authkit-react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { DarkModeProvider } from './components/DarkModeProvider';
import { Login } from './components/Login';
import { FullScreenLoading } from './components/ui/loading';
import { isAPIAuthError } from './lib/data';
import { AuthProvider } from './components/AuthProvider';
import { Button } from './components/ui/button';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => {
        if (isAPIAuthError(error)) {
          return false;
        } else if (count < 3) {
          return true;
        } else {
          return false;
        }
      },
    },
  },
});

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap"
          rel="stylesheet"
        />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <DarkModeProvider>{children}</DarkModeProvider>
          </AuthProvider>
        </QueryClientProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function AuthGate() {
  const { isLoading, user, signOut } = useAuth();

  if (isLoading) {
    return <FullScreenLoading />;
  }

  if (!user) {
    return <Login />;
  }

  if (!user.email?.endsWith('@paramark.com')) {
    return (
      <div className="text-center mt-20">
        <p className="text-lg m-4">Unauthorized email {user.email}</p>
        <Button variant="outline" onClick={() => signOut()}>
          Sign Out
        </Button>
      </div>
    );
  }

  return (
    <div className="transition-colors bg-background text-foreground min-h-screen">
      <Outlet />
      <Toaster richColors />
    </div>
  );
}

export default function App() {
  return <AuthGate />;
}
